import * as React from "react";
import { GridColumns, GridFilterModel, GridRenderCellParams, GridValueGetterParams } from "@mui/x-data-grid-pro";
import { ParticipantSearchResult } from "../../services/types/enterpriseParticipantApiTypes";
import { updateRegistrationGridState } from "../../app/slices/registrationListTableStateSlice";
import {
    CclPersistantDataGridMiscButton,
    CclPersistentDataGrid,
} from "../../components/common/cclPersistentDataGrid/cclPersistentDataGrid";
import { useSelector } from "react-redux";
import { addCustomDataGridFilters } from "../../components/common/customDataGridFilters/customDataGridFilters";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CclCommonLink from "../../components/common/cclCommonLink";
import { eGroupIds, UserClaimsService } from "../../services/currentUserService/currentUserService";

type FilterItem = { columnField: string; operatorValue?: string; value?: any };

function mergeFilterModels(confirmed: GridFilterModel | undefined, incoming: GridFilterModel | undefined): GridFilterModel | undefined {
  if (!confirmed) return incoming ?? undefined;
  if (!incoming) return confirmed;
  const map = new Map<string, FilterItem>();
  (confirmed.items || []).forEach(i => map.set(i.columnField, i));
  (incoming.items || []).forEach(i => map.set(i.columnField, i)); // incoming (props.filter) wins
  return { items: Array.from(map.values()) };
}

function getFullName(params: any) {
    return `${params.row.firstName || ""} ${params.row.lastName || ""}`;
}

const COLUMNS = [
    {
        field: "esiKey",
        headerName: "Registration ID",
        renderCell: (params: GridRenderCellParams) => (
            <CclCommonLink
                text={params.value}
                keyVal={params.value}
                to={`/registration/${params.row.esiKey}`}
            />
        ),
        width: 125,
    },
    {
        field: "name",
        headerName: "Participant",
        renderCell: (params: GridRenderCellParams) => (
            <CclCommonLink
                text={params.value}
                keyVal={params.value}
                to={`/registration/${params.row.esiKey}`}
            />
        ),
        flex: 0.5,
        valueGetter: getFullName,
    },
    {
        field: "emailAddress",
        headerName: "Email",
        type: "string",
        renderCell: (params: GridRenderCellParams) => (
            <CclCommonLink
                text={params.value}
                keyVal={params.value}
                to={`mailto:${params.value}`}
                type={"email"}
            />
        ),
        flex: 0.5,
    },
    {
        field: "esKey",
        headerName: "Session ID",
        type: "string",
        width: 150,
    },
    {
        field: "projectCode",
        headerName: "Project ID",
        type: "string",
        width: 150,
    },
    {
        field: "sessionTitle",
        headerName: "Title",
        type: "string",
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
            <CclCommonLink
                text={params.value}
                keyVal={params.value}
                to={`/session/${params.row.esKey}`}
            />
        ),
    },
    {
        field: "sessionType",
        headerName: "Type",
        type: "string",
        width: 175,
    },
    {
        field: "registrationStatus",
        headerName: "Status",
        width: 150,
        renderCell: (params: GridRenderCellParams) =>
            params.value === "Cancelled" ? (
                <>
                    Cancelled
                    <ErrorOutlineIcon color="error" fontSize="small" sx={{ pl: 1 }} />
                </>
            ) : (
                `${params.value}`
            ),
    },
    {
        field: "registrationDate",
        headerName: "Registered",
        valueGetter: (params: any) =>
            params.row.registrationDate != null
                ? params.row.registrationDate.toString().substring(0, 10)
                : null,
        renderCell: (params: GridRenderCellParams) => {
            if (params.value == null) return <span></span>;
            const [year, month, day] = (params.value as string).split("-");
            return <span>{`${month}/${day}/${year}`}</span>;
        },
        width: 125,
    },
    {
        field: "startDate",
        headerName: "Start",
        valueGetter: (params: any) =>
            params.row.startDate != null
                ? params.row.startDate.toString().substring(0, 10)
                : null,
        renderCell: (params: GridRenderCellParams) => {
            if (params.value == null) return <span></span>;
            const [year, month, day] = (params.value as string).split("-");
            if (year === "9999") return <span></span>;
            return <span>{`${month}/${day}/${year}`}</span>;
        },
        width: 125,
    },
    {
        field: "sessionHostPlatform",
        headerName: "Hosting Platform",
        renderCell: (params: GridRenderCellParams) => <span>{params.row.sessionHostPlatform}</span>,
        width: 200,
    },
    {
        field: "sessionAssessmentPlatform",
        headerName: "Assessment Platform",
        renderCell: (params: GridRenderCellParams) => (
            <span>{params.row.sessionAssessmentPlatform}</span>
        ),
        width: 200,
    },
    {
        field: "sessionHasCompassSku",
        headerName: "Compass",
        valueGetter: (params: GridValueGetterParams) =>
            `${params.row.sessionHasCompassSku ? "Yes" : "No"}`,
        width: 85,
    },
    {
        field: "sessionCustomer",
        headerName: "Customer",
        type: "string",
        width: 175,
    }
];

export interface RegistrationListProps {
    registrations: ParticipantSearchResult[] | undefined;
    loading: boolean;
    hideToolbar?: boolean;
    showResultBar?: boolean;
    resultBarText: string;
    openClientSearch?: () => void;
    filterModel?: GridFilterModel | undefined;
}

const CLIENT_ADMIN_EXCLUDED_FIELDS = new Set([
    "projectCode",
    "startDate",
    "sessionHostPlatform",
    "sessionAssessmentPlatform",
    "sessionHasCompassSku",
]);

export const RegistrationsDataGridPro: React.FC<RegistrationListProps> = (props) => {
    const rawStateSelector = useSelector((state: any) => state.registrationListTableState);
    const isClientAdmin = new UserClaimsService().IsUserInGroups([eGroupIds.eClientAdmin]);
    const columns = isClientAdmin
        ? COLUMNS.filter((x) => !CLIENT_ADMIN_EXCLUDED_FIELDS.has(x.field))
        : COLUMNS;

    const [showConfirmedOnly, setShowConfirmedOnly] = React.useState<boolean>(false);

    let BUTTONS: CclPersistantDataGridMiscButton[] = [
        {
            id: "client-adv-search-button",
            caption: "Search",
            onClick: (rows?: any[]) => props.openClientSearch?.(),
            returnSelected: true,
            restricted: true,
        },
        {
            id: "show-confirmed-only-switch",
            caption: "Show Only Confirmed",
            type: "switch",
            checkedState: showConfirmedOnly,
            onToggled: (checked: boolean) => setShowConfirmedOnly(checked),
            onClick: () => {},
            returnSelected: false,
            restricted: false,
            hidden: false,
        },
    ];

    if (!isClientAdmin) {
        BUTTONS = BUTTONS.filter((x) => x.id !== "client-adv-search-button");
    }

    const stateSelector = React.useMemo(() => {
        if (!isClientAdmin || !rawStateSelector.TableState) return rawStateSelector;
        const tableState = JSON.parse(rawStateSelector.TableState);
        const sortModel = tableState.sorting?.sortModel;
        if (sortModel?.length === 1 && sortModel[0].field === "startDate") {
            return {
                ...rawStateSelector,
                TableState: JSON.stringify({
                    ...tableState,
                    sorting: { sortModel: [{ field: "registrationDate", sort: "desc" }] },
                }),
            };
        }
        return rawStateSelector;
    }, [rawStateSelector, isClientAdmin]);

    const confirmedFilterModel: GridFilterModel | undefined = 
            showConfirmedOnly
                ? {
                      items: [
                          {
                              columnField: "registrationStatus",
                              operatorValue: "equals",
                              value: "Confirmed",
                          },
                      ],
                  }
                : undefined;

    const mergedFilterModel = React.useMemo(
      () => mergeFilterModels(confirmedFilterModel, props.filterModel ?? undefined),
      [confirmedFilterModel, props.filterModel]
    );
    const [activeFilterModel, setActiveFilterModel] = React.useState<GridFilterModel | undefined>(mergedFilterModel);

    React.useEffect(() => {
      setActiveFilterModel(mergedFilterModel);
    }, [mergedFilterModel]);

    return (
        <CclPersistentDataGrid
            columns={addCustomDataGridFilters(columns as GridColumns<any>)}
            data={props.registrations ?? []}
            filterModel={activeFilterModel}
            loading={props.loading}
            stateSelector={stateSelector}
            userPreferenceId={2}
            getRowIdFn={(row: ParticipantSearchResult) => row.esiKey}
            actionCreator={updateRegistrationGridState}
            hideToolbar={props.hideToolbar === true}
            initState={JSON.stringify({
                sorting: { sortModel: [{ field: "registrationDate", sort: "desc" }] },
            })}
            resultBar={props.showResultBar ?? false}
            resultBarText={props.resultBarText}
            hideFooter={false}
            miscButtons={BUTTONS}
            columnVisibility={{
                sessionCustomer: false
            }}
        />
    );
};

export default RegistrationsDataGridPro;
